import React, { useState, useRef, useEffect } from 'react';
import { sophieService, ChatMessage, SophieSource } from '../../services/sophieService';
import { Send, User, Sparkles, ChevronDown, ChevronUp, ChevronRight, FileText, ExternalLink, PanelLeftClose, PanelLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import { useSophieChat, SophieChatProvider } from '../../context/SophieChatContext';
import { ChatSidebar } from './ChatSidebar';

interface CitationCardProps {
    source: SophieSource;
    index: number;
}

const CitationCard: React.FC<CitationCardProps> = ({ source, index }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    // Fallback if content is missing (backward compatibility)
    const content = (source as any).content || "No snippet available.";
    const score = (source as any).score ? Math.round((source as any).score * 100) : null;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-2 transition-all shadow-sm hover:shadow-md">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 dark:bg-indigo-900 rounded flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-xs">
                        {index + 1}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {source.title || `SOP Document`}
                        </p>
                        {score && <p className="text-xs text-green-600 dark:text-green-400 font-mono">{score}% Match</p>}
                    </div>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>
            
            {isOpen && (
                <div className="p-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-xs">
                    <div className="flex items-start gap-2 mb-2 text-gray-600 dark:text-gray-300 italic p-2 bg-white dark:bg-black/20 rounded border border-gray-100 dark:border-gray-800">
                        <FileText size={12} className="mt-0.5 flex-shrink-0" />
                        "{content.substring(0, 300)}..."
                    </div>
                    {source.link && source.link !== '#' && (
                         <a 
                            href={source.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 hover:underline font-medium mt-1"
                        >
                            Open Original Document <ExternalLink size={12} />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};

const CitationSection: React.FC<{ sources: SophieSource[] }> = ({ sources }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="mt-2">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors mb-2"
            >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {sources.length} Trusted Source{sources.length !== 1 ? 's' : ''} Found
            </button>
            
            {isExpanded && (
                <div className="flex flex-col animate-in slide-in-from-top-1 fade-in duration-200">
                    {sources.map((source, idx) => (
                        <CitationCard key={idx} source={source} index={idx} />
                    ))}
                </div>
            )}
        </div>
    );
};

// Inner component that uses the context
const SophieChatInner: React.FC = () => {
  const { profilePictureUrl } = useAuth();
  const { messages, addMessage } = useSophieChat();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const currentInput = input;
    setInput('');
    setIsLoading(true);

    try {
      // Add user message and get the updated messages array
      const userMessage: ChatMessage = { role: 'user', content: currentInput };
      const messagesAfterUser = await addMessage(userMessage);
      
      // Use the returned messages for context (not stale closure)
      const historyPayload = messagesAfterUser.slice(-10);
      const response = await sophieService.querySophie(currentInput, historyPayload);
      
      const botMessage: ChatMessage = {
        role: 'model',
        content: response.answer,
        sources: response.sources
      };
      
      await addMessage(botMessage);
    } catch (error) {
      await addMessage({
        role: 'model',
        content: 'I apologize, but I encountered an error. Please try again in a moment.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isEmptyState = messages.length === 0;

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      {sidebarOpen && <ChatSidebar />}
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full bg-slate-50 dark:bg-gray-900 relative font-sans">
        
        {/* Sidebar Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-3 left-3 z-20 p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
        >
          {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
        </button>
        
        {/* 1. MESSAGES AREA (Only visible if not empty) */}
        {!isEmptyState && (
          <div className="flex-1 overflow-y-auto p-4 pt-14 space-y-6 bg-slate-50 dark:bg-gray-900 scroll-smooth">
              {messages.map((msg, index) => (
              <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                  <div className={`flex gap-4 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  
                  {/* Avatar */}
                  {msg.role === 'user' ? (
                    profilePictureUrl ? (
                      <img 
                        src={profilePictureUrl} 
                        alt="You" 
                        className="flex-shrink-0 w-8 h-8 rounded-full mt-1 shadow-sm border border-gray-100 dark:border-gray-600 object-cover"
                      />
                    ) : (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1 shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-600">
                        <User size={16} />
                      </div>
                    )
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1 shadow-sm bg-black dark:bg-white text-white dark:text-black">
                      <Sparkles size={16} />
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2 min-w-0">
                      {/* Message Bubble */}
                      <div className={`p-4 rounded-2xl shadow-sm leading-relaxed text-[15px] ${
                          msg.role === 'user'
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-tr-md border border-gray-100 dark:border-gray-700'
                          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-tl-md border border-gray-100 dark:border-gray-700'
                      }`}>
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-1">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                              ul: ({node, ...props}) => <ul className="list-disc pl-6 my-2 space-y-1" {...props} />,
                              ol: ({node, ...props}) => <ol className="list-decimal pl-6 my-2 space-y-1" {...props} />,
                              li: ({node, ...props}) => <li className="pl-1" {...props} />,
                            }}
                          >
                              {msg.content}
                          </ReactMarkdown>
                          </div>
                      </div>

                      {/* Citations Area */}
                      {msg.role === 'model' && msg.sources && msg.sources.length > 0 && (
                          <div className="ml-1 max-w-lg">
                              <CitationSection sources={msg.sources} />
                          </div>
                      )}
                  </div>
                  </div>
              </div>
              ))}
              
              {/* Typing Indicator */}
              {isLoading && (
              <div className="flex justify-start animate-pulse ml-12">
                  <div className="flex items-center gap-1 p-2">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                  </div>
              </div>
              )}
              <div ref={messagesEndRef} />
          </div>
        )}

        {/* 2. EMPTY STATE (Centered) */}
        {isEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-gray-900 z-0">
              <div className="mb-16 flex flex-col items-center animate-in zoom-in-95 duration-500">
                  <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center mb-6 shadow-sm border border-gray-200 dark:border-gray-700">
                      <Sparkles size={20} className="text-gray-900 dark:text-white" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">How can I help you today?</h2>
              </div>
          </div>
        )}

        {/* 3. INPUT AREA (Floating or Bottom) */}
        <div className={`
          transition-all duration-500 ease-in-out z-10 w-full
          ${isEmptyState ? 'absolute top-1/2 left-0 mt-8 px-4' : 'relative bg-slate-50 dark:bg-gray-900 p-4'}
        `}>
            <div className="max-w-3xl mx-auto relative">
              <form onSubmit={handleSubmit} className="relative flex items-center">
                  <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Ask anything..."
                      className={`w-full pl-6 pr-12 py-4 bg-white dark:bg-gray-800 border-0 rounded-full focus:ring-0 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] dark:shadow-none placeholder-gray-400 text-gray-900 dark:text-white text-base transition-all`}
                      disabled={isLoading}
                      autoFocus
                  />
                  <button
                      type="submit"
                      disabled={!input.trim() || isLoading}
                      className="absolute right-2 p-2 bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80 disabled:opacity-20 transition-all flex items-center justify-center w-10 h-10 shadow-lg"
                  >
                      <Send size={18} strokeWidth={2.5} />
                  </button>
              </form>
            </div>
            
            {/* Footer Text */}
            <div className="text-center mt-3 animate-in fade-in duration-700">
                <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                    Sophie AI • JLS Trading Co.
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

// Exported component wraps the inner component with the provider
export const SophieChat: React.FC = () => {
  return (
    <SophieChatProvider>
      <SophieChatInner />
    </SophieChatProvider>
  );
};
